import React, { useEffect } from 'react';
import CtyPromptInput, { ClaimDataRenderer, ClaimScoreGeneratorRenderer, InvoiceMatcherRenderer, promptInputType, ErrorBoundary, ClaimIntakeAgentRenderer, getV2ActionOutputKnowledgeSourceIds, ScoreThresholdsProvider, useAgentScoreThresholds } from '@circuitry-ai/doc-ui';
import { HITLWorkflowRenderer } from './HITLWorkflowRenderer';
import { Icon } from '@iconify/react';
import { FeedbackComponent } from '../../../chatadvisor/src/lib/agentconverse/feedback-components';
import { AgentKnowledgeSources } from '@circuitry-ai/doc-chatadvisor';
import { useAppDispatch, updateAgentMessageState } from '@circuitry-ai/doc-data';

export interface AgentExecutionDataRendererProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  claimData: any;
  onButtonAction?: (action: string, buttonData: any) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
}

// Wrap FeedbackComponent with ErrorBoundary
const FeedbackComponentWithErrorBoundary = ErrorBoundary(FeedbackComponent);

export function AgentExecutionDataRenderer({
  isOpen,
  onOpenChange,
  claimData,
  onButtonAction,
  onRefresh,
  isLoading = false
}: Readonly<AgentExecutionDataRendererProps>) {
  const dispatch = useAppDispatch();

  // Agent-config driven score color thresholds for this record's AI worker.
  // Cached per aiworker_id by RTK Query; falls back to defaults (85/60) when absent.
  const { thresholds: scoreThresholds, isReady: areScoreThresholdsReady } = useAgentScoreThresholds(claimData?.[0]?.aiworker_id);

  // Initialize Redux state with existing feedback data when modal opens
  useEffect(() => {
    if (isOpen && claimData?.[0]) {
      const data = claimData[0];
      const messageId = data.message_id || data.aiworker_usage_id;

      // Initialize feedback state
      if (data.feedback_history && data.feedback_history.length > 0) {
        const latestFeedback = data.feedback_history[data.feedback_history.length - 1];
        dispatch(updateAgentMessageState({
          messageId,
          updates: { feedback: latestFeedback.feedback_type }
        }));
      }

      // Initialize comment state by replacing all comments
      if (data.comment_history && data.comment_history.length > 0) {
        dispatch(updateAgentMessageState({
          messageId,
          updates: { comments: data.comment_history }
        }));
      } else {
        // Clear comments if no history
        dispatch(updateAgentMessageState({
          messageId,
          updates: { comments: [] }
        }));
      }
    }
  }, [isOpen, claimData, dispatch]);

  // Feedback-only component
  const FeedbackOnly = () => (
    <FeedbackComponentWithErrorBoundary data={claimData} dataType="claim" />
  );

  // Sources-only component
  const KnowledgeSourcesOnly = () => {
    const data = claimData?.[0];

    const normalizeSourceIds = (value: unknown): any[] => {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      if (typeof value === 'object') return [value];
      return [{ source_id: value }];
    };

    const mergeSourceIds = (...groups: Array<unknown>) => {
      const seen = new Set<string>();
      const merged: any[] = [];

      for (const group of groups) {
        for (const source of normalizeSourceIds(group)) {
          const sourceId = source?.source_id ?? source;
          const key = String(sourceId ?? '');
          if (!key || seen.has(key)) continue;
          seen.add(key);
          merged.push(typeof source === 'object' ? source : { source_id: source });
        }
      }

      return merged;
    };

    const normalizeSourcesArray = (value: unknown): any[] | null => {
      if (!Array.isArray(value) || value.length === 0) return null;
      const valid = value.filter((item) => item?.source_id);
      return valid.length > 0 ? valid : null;
    };

    const extractEstimateParserSources = (epRaw: any): any[] | null => {
      if (!epRaw || typeof epRaw !== 'object') return null;

      const toSourceEntry = (item: any) => ({
        source_id: item.source_id,
        file_name: item.file_name || item.original_file_name,
        ...(item.pages?.length ? { pages: item.pages } : {}),
      });

      if (epRaw.source_id) {
        return [toSourceEntry(epRaw)];
      }

      if (Array.isArray(epRaw.data)) {
        const entries = epRaw.data
          .filter((item: any) => item?.source_id)
          .map(toSourceEntry);
        return entries.length > 0 ? entries : null;
      }

      return null;
    };

    const isClaimIntakeAgent = [
      data?.agent_name,
      data?.input_data?.aiworker_name,
      data?.aiworker_name,
    ].some((name) => (name ?? '').toLowerCase().includes('claim intake'));

    const getSourceIdsFromActionOutput = (actionOutput: any, claimIntakeOnlyPaths = false) => {
      if (!actionOutput) return null;

      const extractSourceIds = (node: any) => {
        if (!node || typeof node !== 'object') return null;
        if (node.pdf_source_ids) return node.pdf_source_ids;
        if (node.source_ids) return node.source_ids;
        if (claimIntakeOnlyPaths && Array.isArray(node.sources)) return node.sources;
        if (node.sources?.source_ids) return node.sources.source_ids;
        if (claimIntakeOnlyPaths && node.sources?.pdf_source_ids) return node.sources.pdf_source_ids;
        if (node.source_data?.pdf_sources) return node.source_data.pdf_sources;
        return null;
      };

      for (const key of Object.keys(actionOutput)) {
        const agentData = actionOutput[key];
        if (Array.isArray(agentData) && agentData.length > 0) {
          const ids = extractSourceIds(agentData[0]);
          if (ids) return ids;
        } else if (agentData && typeof agentData === 'object') {
          const ids = extractSourceIds(agentData);
          if (ids) return ids;
        }
      }
      return null;
    };

    const legacySourceIds =
      data?.source_ids ||
      data?.pdf_source_ids ||
      data?.pdf_source_id ||
      getSourceIdsFromActionOutput(data?.action_output) ||
      data?.action_output?.source_ids ||
      data?.action_output?.pdf_source_ids ||
      data?.action_output?.pdf_source_id ||
      data?.output_data?.source_ids ||
      data?.output_data?.pdf_source_ids ||
      data?.output_data?.pdf_source_id ||
      data?.metadata?.source_ids ||
      data?.metadata?.pdf_source_ids ||
      data?.metadata?.pdf_source_id ||
      data?.source_data?.pdf_sources ||
      getV2ActionOutputKnowledgeSourceIds(data?.action_output) ||
      null;

    const claimIntakeSourceIds = isClaimIntakeAgent
      ? mergeSourceIds(
          extractEstimateParserSources(data?.action_output?.estimateparser),
          normalizeSourcesArray(data?.sources),
          normalizeSourcesArray(data?.input_data?.sources),
          normalizeSourcesArray(data?.action_output?.sources),
          normalizeSourcesArray(data?.metadata?.sources),
          getSourceIdsFromActionOutput(data?.action_output, true),
          legacySourceIds
        )
      : null;

    const sourceIds = isClaimIntakeAgent
      ? (claimIntakeSourceIds ?? [])
      : mergeSourceIds(legacySourceIds);
    const messageId = data?.message_id || data?.aiworker_usage_id;

    if (sourceIds.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 items-center w-full">
        <AgentKnowledgeSources
          sourceIds={sourceIds}
          messageId={messageId}
          showAvailableFilesLabel={isClaimIntakeAgent}
        />
      </div>
    );
  };

  // Helper function to determine which renderer to use
  const renderContent = () => {
    const data = claimData?.[0];
    if (!data) return null;

    const agentName = data?.agent_name?.toLowerCase() || '';
    const inputAgentName = data?.input_data?.aiworker_name?.toLowerCase() || '';
    const aiworkerName = data?.aiworker_name?.toLowerCase() || '';

    const matchesInspectionAgent = (name: string) =>
      name.replace(/_/g, ' ').includes('inspection agent');

    const isClaimScore = agentName.includes("claim score") ||
                        inputAgentName.includes("claim score") ||
                        aiworkerName.includes("claim score");

    const isInspectionAgent = matchesInspectionAgent(agentName) ||
                              matchesInspectionAgent(inputAgentName) ||
                              matchesInspectionAgent(aiworkerName);

    const isVHR = (agentName.includes("vhr")) ||
                        (inputAgentName.includes("vhr")) ||
                        (aiworkerName.includes("vhr"));

    const isInvoiceMatcher = agentName.includes("invoice matcher") || 
                            inputAgentName.includes("invoice matcher") || 
                            aiworkerName.includes("invoice matcher") ||
                            agentName.includes("ro matcher") ||
                            inputAgentName.includes("ro matcher") ||
                            aiworkerName.includes("ro matcher") ||
                            agentName.includes("maintenance claim") ||
                            inputAgentName.includes("maintenance claim") ||
                            aiworkerName.includes("maintenance claim");

      const isClaimIntake = agentName.includes("claim intake") ||
                            inputAgentName.includes("claim intake") ||
                            aiworkerName.includes("claim intake");
      
      // HITL workflow: any agent that has humanInTheLoop in action_output
    if (data?.action_output?.humanInTheLoop !== undefined) {
      return (
        <HITLWorkflowRenderer
          claimData={claimData}
          feedbackComponent={<FeedbackOnly />}
          onRefresh={onRefresh}
        />
      );
    }

    if (isClaimIntake) {
        return (
          <ClaimIntakeAgentRenderer
            claimData={claimData}
            onButtonAction={onButtonAction}
            showActionButtons={true}
            source="agent_history"
            knowledgeComponent={<KnowledgeSourcesOnly />}
            feedbackComponent={<FeedbackOnly />}
            onRefresh={onRefresh}
          />
        );
      }                        

    if (isClaimScore || isVHR || isInspectionAgent) {
      return (
        <ClaimScoreGeneratorRenderer
          claimData={claimData}
          onButtonAction={onButtonAction}
          showActionButtons={true}
          source="agent_history"
          knowledgeComponent={<KnowledgeSourcesOnly />}
          feedbackComponent={<FeedbackOnly />}
          onRefresh={onRefresh}
        />
      );
    }

    if (isInvoiceMatcher) {
      return (
        <InvoiceMatcherRenderer
          claimData={claimData}
          onButtonAction={onButtonAction}
          showActionButtons={true}
          source="agent_history"
          knowledgeComponent={<KnowledgeSourcesOnly />}
          feedbackComponent={<FeedbackOnly />}
          onRefresh={onRefresh}
        />
      );
    }

    return (
      <ClaimDataRenderer
        claimData={claimData}
        onButtonAction={onButtonAction}
        showActionButtons={true}
        source="agent_history"
        knowledgeComponent={<KnowledgeSourcesOnly />}
        feedbackComponent={<FeedbackOnly />}
        onRefresh={onRefresh}
      />
    );
  };


  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <button
          className="fixed inset-0 bg-black bg-opacity-50 z-[42]"
          onClick={() => onOpenChange(false)}
        />
      )}

      {/* Right Side Modal */}
      <div
        className={`fixed top-0 right-0 h-full w-[400px] bg-content1 shadow-xl z-50 transform transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 z-10 p-2 hover:bg-default-200 rounded-full"
          aria-label="Close"
        >
          <Icon icon="mdi:close" className="w-5 h-5 text-default-500" />
        </button>

        {/* ClaimDataRenderer handles all the content */}
        {(isLoading || !areScoreThresholdsReady) ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="text-default-500">Loading usage details...</span>
            </div>
          </div>
        ) : (
          <ScoreThresholdsProvider value={scoreThresholds}>
            {renderContent()}
          </ScoreThresholdsProvider>
        )}


        {/* Comments Input Section */}
        {/* <div className="absolute bottom-0 left-0 right-0 p-4 bg-content1 border-t border-default-200">
          <CtyPromptInput
            elementType={promptInputType.TEXTAREA}
            id="chatinput-textarea"
            classNames={{
              inputWrapper: `!bg-transparent shadow-none`,
              innerWrapper: "relative",
              input: "pt-1 pb-6 pl-2 !pr-10 text-medium",
            }}
            radius="lg"
            variant="flat"
            handleSendClick={() => { }}
            placeholder="Start typing a questin..."
            enableSpeak={true}
            enableSendBtn={true}
            enableImage={true}
            setIsSpeechMode={() => { }}
            isSpeechMode={false}
            uploadedFiles={[]}
            setUploadedFiles={() => { }}
            isDisabled={claimData?.[0]?.agent_name?.toLowerCase().includes("claim score") ||
              claimData?.[0]?.input_data?.aiworker_name?.toLowerCase().includes("claim score") ||
              claimData?.[0]?.aiworker_name?.toLowerCase().includes("claim score") ||
              claimData?.[0]?.agent_name?.toLowerCase().includes("invoice matcher") ||
              claimData?.[0]?.input_data?.aiworker_name?.toLowerCase().includes("invoice matcher") ||
              claimData?.[0]?.aiworker_name?.toLowerCase().includes("invoice matcher")}
          />
        </div> */}
      </div>
    </>
  );
}

export default AgentExecutionDataRenderer; 